import { Search, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useDebounce } from "@/hooks/use-debounce";
import { useState, useEffect } from "react";

export type QAStatusFilter = "all" | "clean" | "has_issues" | "not_reviewed";
export type WPStatusFilter = "all" | "draft" | "publish";
export type QASortOption =
	| "keyword_asc"
	| "date_newest"
	| "most_issues"
	| "least_issues";

export interface QAFilters {
	qaStatus: QAStatusFilter;
	wpStatus: WPStatusFilter;
	search: string;
	sort: QASortOption;
	pageSize: number;
}

const DEFAULT_FILTERS: QAFilters = {
	qaStatus: "all",
	wpStatus: "all",
	search: "",
	sort: "date_newest",
	pageSize: 50,
};

interface QAFilterBarProps {
	filters: QAFilters;
	onFiltersChange: (filters: QAFilters) => void;
}

export function QAFilterBar({ filters, onFiltersChange }: QAFilterBarProps) {
	const [searchInput, setSearchInput] = useState(filters.search);
	const debouncedSearch = useDebounce(searchInput, 300);

	useEffect(() => {
		if (debouncedSearch !== filters.search) {
			onFiltersChange({ ...filters, search: debouncedSearch });
		}
	}, [debouncedSearch]);

	const hasActiveFilters =
		filters.qaStatus !== "all" ||
		filters.wpStatus !== "all" ||
		filters.search !== "" ||
		filters.sort !== "date_newest";

	return (
		<div className="flex flex-wrap items-center gap-3 mb-4">
			<div className="relative flex-1 min-w-[200px] max-w-[300px]">
				<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
				<Input
					placeholder="Search by keyword..."
					value={searchInput}
					onChange={(e) => setSearchInput(e.target.value)}
					className="pl-10"
				/>
			</div>

			<Select
				value={filters.qaStatus}
				onValueChange={(v) =>
					onFiltersChange({ ...filters, qaStatus: v as QAStatusFilter })
				}
			>
				<SelectTrigger className="w-[160px]">
					<SelectValue placeholder="QA Status" />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value="all">All Posts</SelectItem>
					<SelectItem value="clean">Clean (0 issues)</SelectItem>
					<SelectItem value="has_issues">Has Issues</SelectItem>
					<SelectItem value="not_reviewed">Not Reviewed</SelectItem>
				</SelectContent>
			</Select>

			<Select
				value={filters.wpStatus}
				onValueChange={(v) =>
					onFiltersChange({ ...filters, wpStatus: v as WPStatusFilter })
				}
			>
				<SelectTrigger className="w-[140px]">
					<SelectValue placeholder="WP Status" />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value="all">All Statuses</SelectItem>
					<SelectItem value="draft">Draft</SelectItem>
					<SelectItem value="publish">Published</SelectItem>
				</SelectContent>
			</Select>

			<Select
				value={filters.sort}
				onValueChange={(v) =>
					onFiltersChange({ ...filters, sort: v as QASortOption })
				}
			>
				<SelectTrigger className="w-[160px]">
					<SelectValue placeholder="Sort by" />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value="keyword_asc">Keyword A-Z</SelectItem>
					<SelectItem value="date_newest">Date Newest</SelectItem>
					<SelectItem value="most_issues">Most Issues</SelectItem>
					<SelectItem value="least_issues">Least Issues</SelectItem>
				</SelectContent>
			</Select>

			{hasActiveFilters && (
				<Button
					variant="ghost"
					size="sm"
					onClick={() => {
						setSearchInput("");
						onFiltersChange(DEFAULT_FILTERS);
					}}
					className="text-muted-foreground"
				>
					<RotateCcw className="h-3.5 w-3.5 mr-1.5" />
					Reset
				</Button>
			)}

			<div className="ml-auto flex items-center gap-1.5">
				<span className="text-xs text-muted-foreground whitespace-nowrap">Per page</span>
				<Select
					value={String(filters.pageSize)}
					onValueChange={(v) =>
						onFiltersChange({ ...filters, pageSize: Number(v) })
					}
				>
					<SelectTrigger className="w-[72px] h-8 text-xs">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="25">25</SelectItem>
						<SelectItem value="50">50</SelectItem>
						<SelectItem value="100">100</SelectItem>
					</SelectContent>
				</Select>
			</div>
		</div>
	);
}

export { DEFAULT_FILTERS };
